/**
 * Smart Tempo Panel Component
 * UI for displaying tempo detection results and allowing user adjustments
 */

import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../../ui/button';
import { Progress } from '../../ui/progress';
import {
  Activity,
  Zap,
  RefreshCw,
  Check,
  AlertCircle,
  ChevronDown,
  Music2,
  Timer,
  Target,
} from 'lucide-react';
import { detectTempo, analyzeAudioRegions, getTempoConfidenceLabel, suggestTempoRange } from './SmartTempo';

export const SmartTempoPanel = ({
  audioBuffer,
  audioContext,
  currentBpm,
  onTempoChange,
  onClose,
  isVisible = true,
}) => {
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisProgress, setAnalysisProgress] = useState(0);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const analysisRef = useRef(null);

  // Auto-analyze when panel opens with audio
  useEffect(() => {
    if (audioBuffer && audioContext && isVisible && !result && !isAnalyzing) {
      handleAnalyze();
    }
  }, [audioBuffer, audioContext, isVisible]);

  const handleAnalyze = async () => {
    if (!audioBuffer || !audioContext) {
      setError('No audio loaded to analyze');
      return;
    }

    setIsAnalyzing(true);
    setAnalysisProgress(0);
    setError(null);
    setResult(null);

    try {
      // Simulate progress for UX
      const progressInterval = setInterval(() => {
        setAnalysisProgress((prev) => Math.min(prev + 10, 90));
      }, 100);

      // Perform analysis
      const analysisResult = await analyzeAudioRegions(audioBuffer, audioContext, 4);
      
      clearInterval(progressInterval);
      setAnalysisProgress(100);
      
      // Short delay to show 100%
      await new Promise((r) => setTimeout(r, 200));
      
      setResult(analysisResult);
    } catch (err) {
      setError('Failed to analyze tempo: ' + err.message);
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleApplyTempo = () => {
    if (result?.tempo) {
      onTempoChange(result.tempo);
      onClose?.();
    }
  };

  const confidenceInfo = result ? getTempoConfidenceLabel(result.confidence) : null;
  const genreSuggestion = result ? suggestTempoRange(result.tempo) : null;

  if (!isVisible) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      className="bg-zinc-900/95 backdrop-blur-md border border-zinc-700 rounded-lg shadow-xl
        w-80 overflow-hidden"
      data-testid="smart-tempo-panel"
    >
      {/* Header */}
      <div className="px-4 py-3 bg-gradient-to-r from-amber-500/20 to-orange-500/20 border-b border-zinc-700">
        <div className="flex items-center gap-2">
          <Activity className="w-5 h-5 text-amber-400" />
          <h3 className="font-semibold text-white">Smart Tempo</h3>
          <span className="ml-auto text-xs text-zinc-400 font-mono">{currentBpm} BPM</span>
        </div>
        <p className="text-xs text-zinc-400 mt-1">Auto-detect tempo from audio</p>
      </div>

      {/* Content */}
      <div className="p-4">
        {/* Analysis Progress */}
        {isAnalyzing && (
          <div className="mb-4">
            <div className="flex items-center gap-2 mb-2">
              <RefreshCw className="w-4 h-4 text-amber-400 animate-spin" />
              <span className="text-sm text-zinc-300">Analyzing audio...</span>
            </div>
            <Progress value={analysisProgress} className="h-1.5" />
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
            <div className="flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-red-400" />
              <span className="text-sm text-red-300">{error}</span>
            </div>
          </div>
        )}

        {/* Results */}
        {result && !isAnalyzing && (
          <div className="space-y-4">
            {/* Main Result */}
            <div className="text-center py-4 bg-zinc-800/50 rounded-lg">
              <div className="text-4xl font-bold text-white mb-1">
                {result.tempo}
                <span className="text-lg text-zinc-400 ml-1">BPM</span>
              </div>
              <div className="flex items-center justify-center gap-2">
                <Target className="w-4 h-4 text-zinc-500" />
                <span className={`text-sm font-medium ${confidenceInfo?.color}`}>
                  {confidenceInfo?.label} Confidence ({Math.round(result.confidence * 100)}%)
                </span>
              </div>
            </div>

            {/* Genre Suggestion */}
            {genreSuggestion && (
              <div className="flex items-center gap-2 px-3 py-2 bg-zinc-800/50 rounded-lg">
                <Music2 className="w-4 h-4 text-indigo-400" />
                <span className="text-sm text-zinc-300">
                  Matches: <span className="text-indigo-300">{genreSuggestion.name}</span>
                </span>
              </div>
            )}

            {/* Comparison */}
            {currentBpm !== result.tempo && (
              <div className="flex items-center justify-between px-3 py-2 bg-zinc-800/50 rounded-lg">
                <span className="text-sm text-zinc-400">Current:</span>
                <span className="text-sm font-mono text-zinc-300">{currentBpm} BPM</span>
                <span className="text-zinc-600">→</span>
                <span className="text-sm font-mono text-amber-400">{result.tempo} BPM</span>
              </div>
            )}

            {/* Details Toggle */}
            <button
              onClick={() => setShowDetails(!showDetails)}
              className="w-full flex items-center justify-between px-3 py-2 text-sm text-zinc-400
                hover:text-white hover:bg-zinc-800/50 rounded-lg transition-colors"
            >
              <span>Analysis Details</span>
              <ChevronDown className={`w-4 h-4 transition-transform ${showDetails ? 'rotate-180' : ''}`} />
            </button>

            {/* Expanded Details */}
            <AnimatePresence>
              {showDetails && result.regionResults && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  className="overflow-hidden"
                >
                  <div className="space-y-1 text-xs">
                    {result.regionResults.map((region, idx) => (
                      <div
                        key={idx}
                        className="flex items-center justify-between px-3 py-1.5 bg-zinc-800/30 rounded"
                      >
                        <span className="text-zinc-500">Region {idx + 1}</span>
                        <span className="font-mono text-zinc-300">{region.tempo} BPM</span>
                        <span className={getTempoConfidenceLabel(region.confidence).color}>
                          {Math.round(region.confidence * 100)}%
                        </span>
                      </div>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Actions */}
            <div className="flex gap-2 pt-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleAnalyze}
                className="flex-1"
                disabled={isAnalyzing}
              >
                <RefreshCw className={`w-3.5 h-3.5 mr-1 ${isAnalyzing ? 'animate-spin' : ''}`} />
                Re-analyze
              </Button>
              <Button
                size="sm"
                onClick={handleApplyTempo}
                className="flex-1 bg-amber-500 hover:bg-amber-600"
                disabled={!result || result.tempo === currentBpm}
                data-testid="apply-tempo-btn"
              >
                <Check className="w-3.5 h-3.5 mr-1" />
                Apply {result?.tempo} BPM
              </Button>
            </div>
          </div>
        )}

        {/* No Audio State */}
        {!audioBuffer && !isAnalyzing && (
          <div className="text-center py-6 text-zinc-500">
            <Timer className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm">Import audio to detect tempo</p>
          </div>
        )}

        {/* Initial State - Has audio but not analyzed */}
        {audioBuffer && !result && !isAnalyzing && !error && (
          <div className="text-center py-4">
            <Button
              onClick={handleAnalyze}
              className="bg-amber-500 hover:bg-amber-600"
              data-testid="analyze-tempo-btn"
            >
              <Zap className="w-4 h-4 mr-2" />
              Detect Tempo
            </Button>
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default SmartTempoPanel;
